// src/components/ChatbotPreview.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Mic, SendHorizontal, X } from 'lucide-react';

/**
 * Função auxiliar para converter links em markdown para HTML.
 * Exemplo: [Clique aqui](https://google.com) -> <a href="https://google.com" target="_blank" rel="noopener noreferrer">Clique aqui</a>
 */
const renderMessageWithLinks = (content) => {
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  // O terceiro argumento do map é o array que está sendo iterado.
  return content.split(linkRegex).map((part, index, parts) => {
    if (index % 3 === 1) { // O texto do link
      const url = parts[index + 1]; // A URL é o próximo item no array
      return <a key={index} href={url} target="_blank" rel="noopener noreferrer">{part}</a>;
    }
    if (index % 3 === 2) return null; // A URL já foi usada, então não renderizamos nada para ela.
    return part; // Retorna as partes de texto normais.
  });
};

export default function ChatbotPreview({ isOpen, onClose, placeholderPrompt, settings = {}, user, properties }) {
  const intl = useIntl();
  const [inputPrompt, setInputPrompt] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  // Ref para o final da lista de mensagens, para scroll automático
  const messagesEndRef = useRef(null);

  // 🧠 session_id para sessões anônimas
  let sessionId = sessionStorage.getItem("sessionId");
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem("sessionId", sessionId);
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);
  // 🎤 Configuração de voz
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window)) return;
    const SpeechRecognition = window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    const lang = settings?.defaultLanguage;
    recognition.lang =
      lang === 'es' ? 'es-ES' :
      lang === 'en' ? 'en-US' : 'pt-BR';

    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInputPrompt(transcript);
    };

    recognitionRef.current = recognition;
  }, [settings]);

  useEffect(() => {
    if (isOpen) {
      // A saudação inicial é exatamente o que o usuário digita no painel.
      const initialGreeting = placeholderPrompt || "Olá! Como posso ajudar?";
      setMessages([{ role: 'assistant', content: initialGreeting }]);
    }
  }, [isOpen, placeholderPrompt]);

  // 🧠 Envio de mensagem para a API
  const fetchResponse = async (prompt) => {
    // Para o preview dentro do dashboard, a melhor prática é usar o caminho relativo.
    // O Firebase Hosting (configurado em firebase.json) irá reescrever a requisição para o serviço Cloud Run.
    // Isso torna a chamada "same-origin", o que elimina completamente a necessidade de CORS para este caso de uso.

    try {
      const res = await fetch(`/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          user_id: user?.uid || null,
          session_id: sessionId,
          settings: { ...settings, userCity: user?.city || null },
          properties
        })
      });
      const data = await res.json();

      // ✅ Melhoria: Exibe a mensagem de erro específica do backend
      if (data.status === 'error') {
        console.error("❌ Erro retornado pela API do chatbot:", data.message);
        return data.message || intl.formatMessage({ id: "chatbot.errorMessage" });
      }

      return data?.response || intl.formatMessage({ id: "chatbot.errorMessage" });
    } catch (err) {
      console.error("❌ Erro de rede no chatbot preview:", err);
      return intl.formatMessage({ id: "chatbot.errorMessage" });
    }
  };

  const handleSend = async () => {
    if (!inputPrompt.trim()) return;
    const newUserMessage = { role: 'user', content: inputPrompt.trim() };
    setMessages((prev) => [...prev, newUserMessage]);
    setLoading(true);

    try {
      const reply = await fetchResponse(inputPrompt.trim());
      const assistantMessage = { role: 'assistant', content: reply };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: intl.formatMessage({ id: 'chatbotSettings.responseError' }) }
      ]);
    } finally {
      setInputPrompt("");
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleAutoMessage = (e) => {
      const autoMessage = {
        role: 'assistant',
        content: e.detail.content
      };
      setMessages((prev) => [...prev, autoMessage]);
    };

    window.addEventListener("chatbot-auto-message", handleAutoMessage);
    return () => {
      window.removeEventListener("chatbot-auto-message", handleAutoMessage);
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleVoiceInput = () => {
    if (!recognitionRef.current) {
      console.warn("Reconhecimento de voz não disponível.");
      return;
    }
  
    try {
      recognitionRef.current.start();
    } catch (err) {
      console.error("Erro ao iniciar reconhecimento de voz:", err);
    }
  };
  
  if (!isOpen) return null;

  const assistantName = settings.assistantName || 'Assistente Virtual';

  return (
    <div className="chatbot-preview-overlay">
      <div className="chatbot-preview-modal">
        <div className="chatbot-header">
          <div className="chatbot-avatar">
            <span>{assistantName.charAt(0)}</span>
          </div>
          <div className="chatbot-title-container">
            <h3 className="chatbot-title">{assistantName}</h3>
            <p className="chatbot-status">Online</p>
          </div>
          <button className="chatbot-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="chat-history">
          {messages.map((msg, idx) => (
            <div key={idx} className={`message-wrapper ${msg.role}`}>
              <div className="message-bubble">
                {renderMessageWithLinks(msg.content)}
              </div>
            </div>
          ))}
          {loading && (
            <div className="message-wrapper assistant">
              <div className="message-bubble thinking-indicator">
                <div className="dot-flashing"></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot-footer">
          <div className="chat-input-wrapper">
            <textarea
              value={inputPrompt}
              onChange={(e) => setInputPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={intl.formatMessage({ id: 'chatbot.inputPlaceholder' })}
              rows={1}
              className="chat-input"
            />
            <button onClick={handleVoiceInput} disabled={listening} className="chat-icon-btn" title="Falar">
              <Mic size={20} strokeWidth={2} />
            </button>
            <button onClick={handleSend} disabled={loading || !inputPrompt.trim()} className="chat-icon-btn send" title="Enviar">
              <SendHorizontal size={20} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
